import { useMemo, useState } from 'react';
import { previewPatientFeedbackUpload, sendPatientFeedback } from '../api';
import type { PatientFeedbackInput, PatientFeedbackPreviewRow, PatientFeedbackSendRow } from '../types';
import type { WorkBook } from 'xlsx';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const PATIENT_NAME_ALIASES = ['patient name', 'name', 'full name', 'patient', 'patientname'];
const PHONE_ALIASES = ['phone', 'mobile', 'contact number', 'contact', 'mobile number', 'phone number'];
const VISIT_DATE_ALIASES = ['visit date', 'date', 'visitdate', 'appointment date', 'date of visit'];
const DOCTOR_ALIASES = ['doctor', 'consultant', 'physician', 'doctor name'];
const CENTER_ALIASES = ['center', 'centre', 'clinic', 'branch', 'location'];

function normalizeKey(value: unknown) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function pickValue(row: Record<string, unknown>, aliases: string[]) {
  const entries = Object.entries(row);
  for (const [key, value] of entries) {
    if (aliases.some((alias) => normalizeKey(alias) === normalizeKey(key))) {
      const text = String(value ?? '').trim();
      if (text) return text;
    }
  }

  for (const [key, value] of entries) {
    const normalized = normalizeKey(key);
    if (aliases.some((alias) => normalized.includes(normalizeKey(alias)))) {
      const text = String(value ?? '').trim();
      if (text) return text;
    }
  }

  return '';
}

function toPatientInputs(rows: Record<string, unknown>[]): PatientFeedbackInput[] {
  return rows.map((row) => ({
    name: pickValue(row, PATIENT_NAME_ALIASES),
    phone: pickValue(row, PHONE_ALIASES),
    visitDate: pickValue(row, VISIT_DATE_ALIASES),
    doctor: pickValue(row, DOCTOR_ALIASES) || null,
    center: pickValue(row, CENTER_ALIASES) || null,
  }));
}

function isTruthyText(value: unknown) {
  return String(value ?? '').trim().length > 0;
}

type PdfWord = {
  text: string;
  x: number;
  y: number;
};

function splitDelimitedLine(line: string): string[] {
  const trimmed = line.trim();
  if (!trimmed) return [];
  if (trimmed.includes('\t')) return trimmed.split('\t').map((cell) => cell.trim());
  if (trimmed.includes('|')) return trimmed.split('|').map((cell) => cell.trim()).filter(Boolean);
  if (trimmed.includes(',') && trimmed.split(',').length >= 3) return trimmed.split(',').map((cell) => cell.trim());
  if (/\s{2,}/.test(trimmed)) return trimmed.split(/\s{2,}/).map((cell) => cell.trim());
  return [];
}

function groupPdfWords(words: PdfWord[]) {
  const lines: { y: number; words: PdfWord[] }[] = [];

  for (const word of words) {
    const existing = lines.find((line) => Math.abs(line.y - word.y) < 2.5);
    if (existing) {
      existing.words.push(word);
      continue;
    }

    lines.push({ y: word.y, words: [word] });
  }

  return lines
    .sort((a, b) => b.y - a.y)
    .map((line) => ({
      y: line.y,
      words: [...line.words].sort((a, b) => a.x - b.x),
    }));
}

function looksLikeHeader(cells: string[]) {
  const normalized = cells.map((cell) => normalizeKey(cell));
  return normalized.some((cell) => cell.includes('name'))
    && normalized.some((cell) => cell.includes('phone') || cell.includes('mobile'))
    && normalized.some((cell) => cell.includes('date'));
}

function rowsFromStructuredText(text: string): Record<string, unknown>[] {
  const lines = text
    .replace(/\u00a0/g, ' ')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const rows: Record<string, unknown>[] = [];
  let headers: string[] | null = null;

  for (const line of lines) {
    const cells = splitDelimitedLine(line);
    if (!headers && cells.length >= 3 && looksLikeHeader(cells)) {
      headers = cells;
      continue;
    }

    if (headers && cells.length >= 2) {
      const obj: Record<string, unknown> = {};
      headers.forEach((header, index) => {
        if (!header) return;
        obj[header] = String(cells[index] ?? '').trim();
      });
      if (Object.values(obj).some((value) => String(value ?? '').trim())) {
        rows.push(obj);
      }
    }
  }

  if (rows.length) {
    return rows;
  }

  for (const line of lines) {
    if (!/(\b\d{10}\b|\+91\d{10}\b|\b91\d{10}\b)/.test(line)) {
      continue;
    }

    const parts = line.split(/\s{2,}|\t|\|/).map((part) => part.trim()).filter(Boolean);
    if (parts.length < 2) {
      continue;
    }

    const obj: Record<string, unknown> = {
      Name: parts[0],
      Phone: parts.find((part) => /(\b\d{10}\b|\+91\d{10}\b|\b91\d{10}\b)/.test(part)) || '',
      Date: parts.find((part) => /\b\d{4}-\d{2}-\d{2}\b/.test(part) || /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/.test(part)) || '',
      Doctor: '',
      Center: '',
    };

    if (Object.values(obj).some((value) => String(value ?? '').trim())) {
      rows.push(obj);
    }
  }

  return rows;
}

function extractVisitDateFromPdf(lines: string[]): string {
  const headerLine = lines.find((line) => /schedule for/i.test(line) && / on /i.test(line));
  const match = headerLine?.match(/on\s+(\d{1,2}\s+[A-Za-z]{3,9},\s+\d{4})/i);
  if (!match) {
    return '';
  }

  const parsed = new Date(match[1]);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return match[1];
}

function parsePdfAppointmentRows(words: PdfWord[]): Record<string, unknown>[] {
  const groupedLines = groupPdfWords(words);
  const lines = groupedLines.map((line) => line.words.map((word) => word.text).join(' ').replace(/\s+/g, ' ').trim());
  const visitDate = extractVisitDateFromPdf(lines);
  const rows: Record<string, unknown>[] = [];
  const timeRowRegex = /^\d{1,2}:\d{2}\s*(?:AM|PM)\s*-\s*\d{1,2}:\d{2}\s*(?:AM|PM)\b/i;

  for (const line of groupedLines) {
    const text = line.words.map((word) => word.text).join(' ').replace(/\s+/g, ' ').trim();
    if (!timeRowRegex.test(text)) {
      continue;
    }

    const phoneWord = line.words.find((word) => /^(?:\+?91\d{10}|[6-9]\d{9})$/.test(word.text.replace(/\s+/g, '')));
    if (!phoneWord) {
      continue;
    }

    const patientWords = line.words.filter((word) => word.x >= 250 && word.x < phoneWord.x - 2);
    let name = patientWords.map((word) => word.text).join(' ').replace(/\s+/g, ' ').trim();
    name = name.replace(/\s*\(P\d+\)\s*$/i, '').trim();
    name = name.replace(/\s*\(P\d+\)\s+/gi, ' ').trim();

    if (!name) {
      const afterDoctor = line.words
        .filter((word) => word.x >= 220 && word.x < phoneWord.x - 2)
        .map((word) => word.text)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^Dr\s+[A-Za-z.\-]+\s+[A-Za-z.\-]+\s+/i, '')
        .replace(/\s*\(P\d+\)\s*$/i, '')
        .trim();
      name = afterDoctor;
    }

    if (!name) {
      continue;
    }

    rows.push({
      Name: name,
      Phone: phoneWord.text.replace(/\s+/g, '').trim(),
      Date: visitDate,
      Doctor: '',
      Center: '',
    });
  }

  return rows;
}

async function parseSpreadsheet(file: File): Promise<Record<string, unknown>[]> {
  const XLSX = await import('xlsx');
  const ext = file.name.split('.').pop()?.toLowerCase();
  let workbook: WorkBook;

  if (ext === 'pdf') {
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.mjs', import.meta.url).toString();
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: buffer }).promise;
    const pageNumbers = Array.from({ length: pdf.numPages }, (_, index) => index + 1);
    const pageWords = await Promise.all(pageNumbers.map(async (pageNum) => {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const words: PdfWord[] = [];
      for (const item of content.items as any[]) {
        const text = String(item.str ?? '').replace(/\s+/g, ' ').trim();
        if (!text) continue;
        const transform = item.transform || [];
        const x = Number(transform[4] ?? 0);
        const y = Number(transform[5] ?? 0);
        words.push({ text, x, y });
      }
      return words;
    }));
    const words = pageWords.flat();

    const structuredRows = parsePdfAppointmentRows(words);
    if (structuredRows.length) {
      return structuredRows;
    }

    const fallbackText = groupPdfWords(words).map((line) => line.words.map((word) => word.text).join(' ')).join('\n');
    return rowsFromStructuredText(fallbackText);
  }

  if (ext === 'csv') {
    const text = await file.text();
    workbook = XLSX.read(text, { type: 'string' });
  } else {
    const buffer = await file.arrayBuffer();
    workbook = XLSX.read(buffer, { type: 'array' });
  }

  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    return [];
  }

  const sheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false }) as unknown[][];
  if (!rows.length) {
    return [];
  }

  const headers = rows[0].map((cell) => String(cell ?? '').trim());
  const dataRows = rows.slice(1).filter((row) => row.some((cell) => isTruthyText(cell)));

  return dataRows.map((row) => {
    const obj: Record<string, unknown> = {};
    headers.forEach((header, index) => {
      if (!header) return;
      obj[header] = String(row[index] ?? '').trim();
    });
    return obj;
  });
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={`rounded-2xl border px-3 py-2 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export default function PatientFeedbackPanel() {
  const [status, setStatus] = useState<LoadState>('idle');
  const [fileName, setFileName] = useState('');
  const [previewRows, setPreviewRows] = useState<PatientFeedbackPreviewRow[]>([]);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [forceDuplicates, setForceDuplicates] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendResults, setSendResults] = useState<PatientFeedbackSendRow[]>([]);
  const [sendSummary, setSendSummary] = useState<{ sent: number; failed: number; duplicate: number } | null>(null);

  const visibleRows = useMemo(
    () => previewRows.filter((row) => !excludedRows.has(row.rowIndex)),
    [excludedRows, previewRows]
  );

  const validRows = visibleRows.filter((row) => row.status === 'valid');
  const invalidRows = visibleRows.filter((row) => row.status === 'invalid');
  const duplicateRows = visibleRows.filter((row) => row.status === 'duplicate');
  const selectedRows = visibleRows.filter(
    (row) => row.status === 'valid' || (forceDuplicates && row.status === 'duplicate')
  );

  const totalCount = visibleRows.length;
  const sentCount = sendSummary?.sent ?? 0;
  const failedCount = sendSummary?.failed ?? 0;

  async function handleFile(file: File | null) {
    if (!file) return;
    setStatus('loading');
    setError(null);
    setSendResults([]);
    setSendSummary(null);
    setFileName(file.name);
    setExcludedRows(new Set());

    try {
      const rawRows = await parseSpreadsheet(file);
      const patients = toPatientInputs(rawRows);
      const preview = await previewPatientFeedbackUpload(patients);
      setPreviewRows(preview.rows);
      setStatus('ready');
    } catch (e) {
      setStatus('error');
      setError(e instanceof Error ? e.message : 'Failed to parse spreadsheet');
      setPreviewRows([]);
    }
  }

  function toggleRow(rowIndex: number) {
    setExcludedRows((current) => {
      const next = new Set(current);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  }

  async function sendNow() {
    if (!selectedRows.length) {
      setError('No valid rows selected for sending.');
      return;
    }

    setSending(true);
    setError(null);
    setConfirmOpen(false);

    try {
      const patients = selectedRows.map((row) => ({
        name: row.name,
        phone: row.normalizedPhone || row.phone,
        visitDate: row.visitDate,
        center: row.center || null,
        doctor: row.doctor || null,
      }));

      const result = await sendPatientFeedback(patients, forceDuplicates);
      setSendResults(result.results);
      setSendSummary({
        sent: result.sent,
        failed: result.failed,
        duplicate: result.duplicate,
      });
      setStatus('ready');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send feedback WhatsApp messages');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Walk-in Patient Feedback</h3>
            <p className="mt-1 text-xs text-slate-500">
              Upload a Practo CSV/XLSX export, review the rows, and send English + Odia WhatsApp feedback templates.
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center rounded-full bg-wechat-accent px-4 py-2 text-sm font-semibold text-white">
            {status === 'loading' ? 'Parsing…' : 'Upload CSV / Excel / PDF'}
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              disabled={status === 'loading' || sending}
            />
          </label>
        </div>

        {fileName && (
          <div className="mt-3 rounded-xl bg-white px-3 py-2 text-xs text-slate-600">
            File: <span className="font-medium text-slate-900">{fileName}</span>
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-6">
          <SummaryCard label="Total" value={totalCount} tone="bg-white" />
          <SummaryCard label="Valid" value={validRows.length} tone="bg-emerald-50" />
          <SummaryCard label="Invalid" value={invalidRows.length} tone="bg-rose-50" />
          <SummaryCard label="Duplicate" value={duplicateRows.length} tone="bg-amber-50" />
          <SummaryCard label="Sent" value={sentCount} tone="bg-sky-50" />
          <SummaryCard label="Failed" value={failedCount} tone="bg-slate-100" />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={forceDuplicates}
              onChange={(e) => setForceDuplicates(e.target.checked)}
              className="rounded border-slate-300"
            />
          Allow duplicate sends again today
          </label>
          <button
            onClick={() => setExcludedRows(new Set(previewRows.filter((row) => row.status !== 'valid').map((row) => row.rowIndex)))}
            className="rounded-full border border-slate-300 px-3 py-1 font-medium text-slate-700"
          >
            Remove invalid / duplicate rows
          </button>
          <button
            onClick={() => setExcludedRows(new Set())}
            className="rounded-full border border-slate-300 px-3 py-1 font-medium text-slate-700"
          >
            Reset selection
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            Selected for send: <span className="font-semibold text-slate-900">{selectedRows.length}</span>
          </div>
          <button
            disabled={sending || !selectedRows.length}
            onClick={() => setConfirmOpen(true)}
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send Feedback WhatsApp'}
          </button>
        </div>
      </section>

      {error && <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}

      {previewRows.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h4 className="text-sm font-semibold text-slate-900">Preview Rows</h4>
            <p className="text-xs text-slate-500">
              Remove any invalid or duplicate rows before sending. Existing same-day sends are flagged by the server.
            </p>
          </div>

          <div className="max-h-[34rem] overflow-auto">
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="text-slate-500">
                  <th className="px-3 py-2">Keep</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Visit Date</th>
                  <th className="px-3 py-2">Center</th>
                  <th className="px-3 py-2">Doctor</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Issues</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {previewRows.map((row) => {
                  const removed = excludedRows.has(row.rowIndex);
                  return (
                    <tr key={row.rowIndex} className={removed ? 'bg-slate-50 opacity-60' : ''}>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => toggleRow(row.rowIndex)}
                          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                            removed ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-700'
                          }`}
                        >
                          {removed ? 'Removed' : 'Keep'}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-900">{row.name || 'Missing'}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">{row.normalizedPhone || row.phone}</td>
                      <td className="px-3 py-2 text-slate-700">{row.visitDate || 'Missing'}</td>
                      <td className="px-3 py-2 text-slate-700">{row.center || '—'}</td>
                      <td className="px-3 py-2 text-slate-700">{row.doctor || '—'}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                            row.status === 'valid'
                              ? 'bg-emerald-100 text-emerald-700'
                              : row.status === 'duplicate'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-rose-100 text-rose-700'
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-500">
                        {row.issues.length ? row.issues.join(', ') : 'OK'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {sendResults.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h4 className="text-sm font-semibold text-slate-900">Send Results</h4>
            <p className="text-xs text-slate-500">
              English and Odia statuses are shown separately for each patient.
            </p>
          </div>

          <div className="max-h-[28rem] overflow-auto">
            <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="text-slate-500">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">English</th>
                  <th className="px-3 py-2">Odia</th>
                  <th className="px-3 py-2">Duplicate</th>
                  <th className="px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sendResults.map((row, index) => (
                  <tr key={`${row.phone}-${index}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                    <td className="px-3 py-2 font-mono text-slate-700">{row.phone}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                        row.englishStatus === 'sent'
                          ? 'bg-emerald-100 text-emerald-700'
                          : row.englishStatus === 'blocked_duplicate'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-rose-100 text-rose-700'
                      }`}>
                        {row.englishStatus}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                        row.odiaStatus === 'sent'
                          ? 'bg-emerald-100 text-emerald-700'
                          : row.odiaStatus === 'blocked_duplicate'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-rose-100 text-rose-700'
                      }`}>
                        {row.odiaStatus}
                      </span>
                    </td>
                    <td className="px-3 py-2">{row.duplicate ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-slate-500">{row.error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">Confirm send</h3>
            <p className="mt-2 text-sm text-slate-600">
              This will send English and Odia feedback messages to {selectedRows.length} patients.
            </p>
            <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={forceDuplicates}
                onChange={(e) => setForceDuplicates(e.target.checked)}
                className="mt-1 rounded border-slate-300"
              />
              <span>
                Allow duplicate sends again today if any selected phone has already been messaged.
              </span>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={sendNow}
                disabled={sending}
                className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Send now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
